import type { GradedAnswer, MarkedPiece } from "./answer-marks.js";
import { type Rating, ratings } from "./rating.js";
import { wordCharacters } from "./word-characters.js";

/**
 * Marking typed characters against the word a card is about.
 *
 * What `production` and `listening` both ask for. One starts from the English
 * and one from a recording, and both want the same characters back, so both
 * are marked here.
 *
 * **A word is graded whole, not per character.** 突然 typed as 突燃 is not half
 * right: it is a different word, and the reader could not have written the one
 * they were asked for. Marks are still reported per character so the page can
 * show where it went wrong.
 *
 * **There is no middle between one character and another.** Pinyin has one,
 * and it is the whole reason this site can grade Chinese better than a string
 * comparison, because right sounds under the wrong tone is a real degree of
 * knowing. A threshold invented here over characters ("one of four") would put
 * a number on the schedule that nothing measured.
 *
 * **The other writing is the one middle characters do have.** A reader who
 * answers 說話 to a card about 说话 knew the word and wrote it in the other
 * script, which is neither the answer nor a failure to produce one. That earns
 * `hard`, the same rating pinyin gives for the right sounds under the wrong
 * tone, and it is the only way a set of characters reaches it. See
 * docs/fuxi.md.
 *
 * **A word that sounds the same is right, and only a listening card has
 * one.** 事 and 是 are both shì, and a reader who has been played the word and
 * nothing else cannot tell which of them they were asked for. Marking that
 * wrong would be marking them down for something the card never told them, so
 * it earns `good` outright rather than the `hard` the other writing gets. The
 * card still says which word it was about, so nobody is left thinking 是 was
 * the headword. `src/user/wider-marking.ts` is what finds them.
 */

/** How one typed character came out against the one expected there. */
function markedPieces(properties: {
  expected: readonly string[];
  typed: readonly string[];
}): readonly MarkedPiece[] {
  const { expected, typed } = properties;
  const length = Math.max(expected.length, typed.length);

  return Array.from({ length }, (_, at) => ({
    mark: markOf(expected[at], typed[at]),
    ...(expected[at] === undefined ? {} : { expected: expected[at] }),
    ...(typed[at] === undefined ? {} : { typed: typed[at] }),
  }));
}

/** How one character came out. */
function markOf(
  expected: string | undefined,
  typed: string | undefined,
): MarkedPiece["mark"] {
  if (expected === undefined) {
    return "extra";
  }

  if (typed === undefined) {
    return "missing";
  }

  return expected === typed ? "correct" : "wrong";
}

/**
 * The rating typed characters earn.
 *
 * Three of the four. `easy` stays out, nothing in a set of characters being
 * able to say a reader found it easy, which is the rule
 * `src/fuxi/answer-marks.ts` states for every grader here.
 */
function ratingOf(pieces: readonly MarkedPiece[]): Rating {
  return pieces.every((piece) => piece.mark === "correct")
    ? ratings.good
    : ratings.again;
}

/**
 * The word as a reader typed it, marked correct against itself.
 *
 * What the other writing earns. The characters they wrote are the characters
 * of a real form of the word, so marking them against the form the card
 * happened to ask for would put a red cross under every one of them.
 */
function asWritten(written: string): readonly MarkedPiece[] {
  return wordCharacters(written).map((character) => ({
    mark: "correct" as const,
    expected: character,
    typed: character,
  }));
}

/**
 * Mark typed characters against the word a card is about.
 *
 * **Whitespace either side is dropped and nothing else is.** A reader typing
 * into a field with an input method open collects a stray space often enough
 * to be worth forgiving, and a space inside a word is a real mistake.
 *
 * **What would have been accepted stays the one form the card asked for**,
 * even where another was marked. A reader who wrote the other writing or a
 * word that sounds the same is being shown the one this entry is published
 * under, which is the thing they did not have. `src/user/wider-marking.ts` is
 * what finds both.
 *
 * **The sound is tried first.** A card played to a reader takes the other
 * writing as right rather than as nearly right, both being read the same way,
 * and only one of the two lists is ever filled for a given card anyway.
 *
 * @param properties - The word expected, what was typed, and what else a card
 * of this question would take.
 * @returns The rating, the marks, and what would have been accepted.
 */
export function gradeHanzi(properties: {
  expected: string;
  typed: string;
  otherWritings?: readonly string[];
  sameSound?: readonly string[];
}): GradedAnswer {
  const typed = properties.typed.trim();
  const accepted = [properties.expected];

  if ((properties.sameSound ?? []).includes(typed)) {
    return { rating: ratings.good, pieces: asWritten(typed), accepted };
  }

  if ((properties.otherWritings ?? []).includes(typed)) {
    return { rating: ratings.hard, pieces: asWritten(typed), accepted };
  }

  const pieces = markedPieces({
    expected: wordCharacters(properties.expected),
    typed: wordCharacters(typed),
  });

  return { rating: ratingOf(pieces), pieces, accepted };
}
